// Modal de extensão de trial — Story 3.9 AC3.

import * as Dialog from '@radix-ui/react-dialog';
import { ArrowRight, Sparkles, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { addDaysLocal, daysDiffLocal, formatBR, parseLocalDate } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Input, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { useExtendTrial } from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';
import type { SubscriptionDetail } from '../../lib/subscriptions-api';

interface ExtendTrialModalProps {
  subscription: SubscriptionDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExtendTrialModal({
  subscription,
  open,
  onOpenChange,
}: ExtendTrialModalProps): JSX.Element | null {
  const [newTrialEndsAt, setNewTrialEndsAt] = useState('');
  const [reason, setReason] = useState('');
  const extendMutation = useExtendTrial();

  useEffect(() => {
    if (open) {
      // Default: +7 dias do trial atual
      if (subscription?.trialEndsAt) {
        const currentYmd = subscription.trialEndsAt.slice(0, 10);
        setNewTrialEndsAt(addDaysLocal(currentYmd, 7));
      }
      setReason('');
      extendMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subscription]);

  if (!subscription || !subscription.trialEndsAt) return null;

  const currentYmd = subscription.trialEndsAt.slice(0, 10);
  const minDate = addDaysLocal(currentYmd, 1);
  const reasonValid = reason.trim().length >= 5 && reason.trim().length <= 500;
  const dateValid = newTrialEndsAt > currentYmd;
  const canSubmit = dateValid && reasonValid && !extendMutation.isPending;
  const daysAdded = dateValid ? daysDiffLocal(currentYmd, newTrialEndsAt) : 0;

  async function handleConfirm(): Promise<void> {
    if (!canSubmit || !subscription) return;
    try {
      await extendMutation.mutateAsync({
        id: subscription.id,
        payload: {
          new_trial_ends_at: newTrialEndsAt,
          reason: reason.trim(),
        },
      });
      toast.success('Trial estendido.', {
        description: `Novo fim: ${formatBR(parseLocalDate(newTrialEndsAt))}`,
      });
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao estender trial.'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(520px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="flex items-center gap-2 text-[18px] font-semibold tracking-[-0.005em] text-ink">
                <Sparkles size={16} strokeWidth={1.8} className="text-warn" />
                Estender trial
              </Dialog.Title>
              <p className="mt-1 text-[12px] text-muted">
                {subscription.client.name} · {subscription.plan?.name ?? '—'}
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
            className="flex flex-col gap-4"
          >
            <Field
              label="Novo fim do trial"
              required
              hint={`Deve ser depois de ${formatBR(parseLocalDate(currentYmd))}`}
              error={
                newTrialEndsAt && !dateValid ? 'Data deve ser depois do fim atual.' : undefined
              }
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="date"
                  min={minDate}
                  value={newTrialEndsAt}
                  onChange={(e) => setNewTrialEndsAt(e.target.value)}
                />
              )}
            </Field>

            {/* Preview "Atual → Novo (+N dias)" */}
            {dateValid && (
              <div className="flex items-center justify-between rounded-[10px] border border-line bg-panel-2 px-4 py-3 text-[13px]">
                <div className="text-muted">
                  Atual:{' '}
                  <strong className="text-ink">{formatBR(parseLocalDate(currentYmd))}</strong>
                </div>
                <ArrowRight size={14} strokeWidth={1.8} className="text-muted-3" />
                <div className="text-muted">
                  Novo:{' '}
                  <strong className="text-ink">{formatBR(parseLocalDate(newTrialEndsAt))}</strong>
                </div>
                <div className="text-[12px] text-good">
                  +{daysAdded} {daysAdded === 1 ? 'dia' : 'dias'}
                </div>
              </div>
            )}

            <Field
              label="Motivo"
              required
              hint="Min. 5 / max. 500 caracteres"
              error={
                reason.length > 0 && !reasonValid
                  ? reason.trim().length < 5
                    ? 'Mínimo 5 caracteres.'
                    : 'Máximo 500 caracteres.'
                  : undefined
              }
            >
              {({ inputId }) => (
                <Textarea
                  id={inputId}
                  rows={3}
                  maxLength={500}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex: Cliente pediu mais tempo pra avaliar / negociação retenção"
                />
              )}
            </Field>

            <div className="mt-2 flex items-center justify-end gap-2">
              <PillButton type="button" onClick={() => onOpenChange(false)}>
                Cancelar
              </PillButton>
              <PillButton type="submit" solid disabled={!canSubmit}>
                {extendMutation.isPending ? 'Estendendo…' : 'Confirmar extensão'}
              </PillButton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
